import React, { useState, useEffect } from 'react';
import LocationDropdown from '../../../tabs/Dropdown/LocationName';
import AreaDropdown from '../../../tabs/Dropdown/AreaDropdown';
import axiosInstance from '../../../../../api';

// Predefined list of injury types for the datalist
const injuryTypes = [
  'Fracture',
  'Burn',
  'Cardiac Arrest',
  'Stroke',
  'Head Injury',
  'Respiratory Distress',
];

const AddCaseModal = ({ show, onClose, onSave, isLoading }) => {

  // 1. STATE

  const [errors, setErrors] = useState({});
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [medicalCenters, setMedicalCenters] = useState([]);
  const [selectedMedicalCenter, setSelectedMedicalCenter] = useState('');
  const [accidentPlace, setAccidentPlace] = useState('');


  // **NEW** – shared patient location / area (same for every patient)
  const [patientLocation, setPatientLocation] = useState('');
  const [patientArea, setPatientArea] = useState('');

  // patients now contain *only* per-patient fields
  const [patients, setPatients] = useState([
    {
      case_name: '',
      injury_type: '',
      age: '',
      gender: '',
      contact_number: '',
      triage_card: '',
    },
  ]);


  // 2. RESET FORM WHEN MODAL OPENS

  useEffect(() => {
    if (show) {
      setSelectedLocation('');
      setSelectedArea('');
      setSelectedMedicalCenter('');
      setMedicalCenters([]);
      setPatientLocation('');
      setPatientArea('');
      setPatients([
        {
          case_name: '',
          injury_type: '',
          age: '',
          gender: '',
          contact_number: '',
          triage_card: '',
        },
      ]);
      setErrors({});
      sessionStorage.removeItem('selectedAreaId');
    }
  }, [show]);

  // 3. LOCATION / AREA / MEDICAL CENTER HANDLERS
  const handleLocationChange = (e) => {
    const locId = e.target.value;
    setSelectedLocation(locId);
    setSelectedArea('');
    setMedicalCenters([]);
    setSelectedMedicalCenter('');
    sessionStorage.removeItem('selectedAreaId');
    setErrors((prev) => ({ ...prev, case_location: '' }));
  };

  const handleAreaChange = async (e) => {
    const areaId = e.target.value;
    setSelectedArea(areaId);
    sessionStorage.setItem('selectedAreaId', areaId);
    setErrors((prev) => ({ ...prev, case_area: '' }));

    if (areaId) {
      try {
        const authToken = sessionStorage.getItem('auth_token');
        const res = await axiosInstance.get(
          `/ems/get-nearby-medical-center-by-area/${areaId}`,
          {
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setMedicalCenters(res.data || []);
      } catch (err) {
        console.error('Error fetching medical centers:', err);
        setMedicalCenters([]);
      }
    }
  };

  const handleMedicalCenterChange = (e) => {
    const mcId = e.target.value;
    setSelectedMedicalCenter(mcId);
    setErrors((prev) => ({ ...prev, nearest_medical_center: '' }));
  };


  // 4. PATIENT-LOCATION / PATIENT-AREA HANDLERS (shared)

  const handlePatientLocationChange = (e) => {
    const locId = e.target.value;
    setPatientLocation(locId);
    setPatientArea(''); // reset area when location changes
    setErrors((prev) => ({ ...prev, patient_location_0: '' }));
  };

  const handlePatientAreaChange = (e) => {
    const areaId = e.target.value;
    setPatientArea(areaId);
    setErrors((prev) => ({ ...prev, patient_area_0: '' }));
  };


  // 5. PER-PATIENT FIELD HANDLERS

  const handlePatientChange = (e, index) => {
    const { name, value } = e.target;
    const updated = [...patients];

    // ── field-specific live validation ──
    if (name === 'case_name' || name === 'injury_type') {
      if (/^[a-zA-Z\s]*$/.test(value) || value === '') {
        updated[index][name] = value;
        setErrors((prev) => ({ ...prev, [`${name}_${index}`]: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [`${name}_${index}`]: `${
            name === 'case_name' ? 'Case Name' : 'Injury Type'
          } must contain only letters and spaces`,
        }));
        return;
      }
    } else if (name === 'contact_number') {
      if (/^\d*$/.test(value) || value === '') {
        updated[index][name] = value;
        setErrors((prev) => ({ ...prev, [`${name}_${index}`]: '' }));
      } else {
        setErrors((prev) => ({
          ...prev,
          [`${name}_${index}`]: 'Contact Number must contain only numbers',
        }));
        return;
      }
    } else {
      // age / gender / triage_card
      updated[index][name] = value;
      setErrors((prev) => ({ ...prev, [`${name}_${index}`]: '' }));
    }


    setPatients(updated);
  };


  // 6. ADD / REMOVE PATIENT

  const addPatient = () => {
    setPatients([
      ...patients,
      {
        case_name: '',
        injury_type: '',
        age: '',
        gender: '',
        contact_number: '',
        triage_card: '',
      },
    ]);
  };
  

  const removePatient = (index) => {
    setPatients(patients.filter((_, i) => i !== index));
  };


  // 7. FORM VALIDATION

  const validateForm = () => {
  const newErrors = {};
  let ok = true;

  // ---- case level ----
  if (!selectedLocation) {
    newErrors.case_location = 'Location is required';
    ok = false;
  }
  if (!selectedArea) {
    newErrors.case_area = 'Area is required';
    ok = false;
  }
  if (!selectedMedicalCenter) {
    newErrors.nearest_medical_center = 'Medical center is required';
    ok = false;
  }

  // ---- shared patient fields ----
  if (!patientLocation) {
    newErrors.patient_location_0 = 'Patient Location is required';
    ok = false;
  }
  if (!patientArea) {
    newErrors.patient_area_0 = 'Patient Area is required';
    ok = false;
  }
  if (!accidentPlace) {
    newErrors.accident_place = 'Accident Place is required';
    ok = false;
  }

  // ---- per-patient validations ----
  patients.forEach((p, i) => {
    if (!p.case_name) {
      newErrors[`case_name_${i}`] = 'Case Name is required';
      ok = false;
    } else if (!/^[a-zA-Z\s]+$/.test(p.case_name)) {
      newErrors[`case_name_${i}`] =
        'Case Name must contain only letters and spaces';
      ok = false;
    }

    if (!p.injury_type) {
      newErrors[`injury_type_${i}`] = 'Injury Type is required';
      ok = false;
    } else if (!/^[a-zA-Z\s]+$/.test(p.injury_type)) {
      newErrors[`injury_type_${i}`] =
        'Injury Type must contain only letters and spaces';
      ok = false;
    }

    if (!p.triage_card) {
      newErrors[`triage_card_${i}`] = 'Triage Card is required';
      ok = false;
    }

    if (p.contact_number && !/^\d+$/.test(p.contact_number)) {
      newErrors[`contact_number_${i}`] =
        'Contact Number must contain only numbers';
      ok = false;
    }
  });

  setErrors(newErrors);
  return ok;
};



  // 8. SUBMIT → CALL onSave (parent handles API)

  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      user_id: sessionStorage.getItem('user_id'),
      case_location: selectedLocation,
      case_area: selectedArea,
      nearest_medical_center: selectedMedicalCenter,

      // **IMPORTANT** – attach shared location/area to every patient
      patient_location: patientLocation,
      patient_area: patientArea,
      accident_place: accidentPlace,


      patients: patients.map((p) => ({
      case_name: p.case_name,
      injury_type: p.injury_type,
      age: p.age || null,
      gender: p.gender || null,
      contact_number: p.contact_number || null,
      triage_card: p.triage_card,   // ➤ added
    })),
    };

    try {
      await onSave(payload);
      sessionStorage.removeItem('selectedAreaId');
    } catch (err) {
      console.error('Error saving case:', err);
      alert(`Error: ${err.response?.data?.message || 'Failed to save case'}`);
    }
  };


  // 9. RENDER

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          {/* ── HEADER ── */}
          <div className="modal-header">
            <h5 className="modal-title">Add EMS Case</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isLoading}
            />
          </div>

          {/* ── BODY ── */}
          <div className="modal-body">
            {/* ── CASE LOCATION / AREA / MEDICAL CENTER ── */}
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">
                  Location<span className="text-danger">*</span>
                </label>
                <LocationDropdown
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  name="case_location"
                />
                {errors.case_location && (
                  <div className="text-danger">{errors.case_location}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  Area<span className="text-danger">*</span>
                </label>
                <AreaDropdown
                  locationId={selectedLocation}
                  value={selectedArea}
                  onChange={handleAreaChange}
                  name="case_area"
                />
                {errors.case_area && (
                  <div className="text-danger">{errors.case_area}</div>
                )}
              </div>

              <div className="col-12">
                <label className="form-label">
                  Medical Center<span className="text-danger">*</span>
                </label>
                <select
                  value={selectedMedicalCenter}
                  onChange={handleMedicalCenterChange}
                  className="form-select"
                  disabled={!medicalCenters.length}
                >
                  <option value="">Select Medical Center</option>
                  {medicalCenters.map((mc) => (
                    <option key={mc.id} value={mc.id}>
                      {mc.name} ({mc.distance_km} km)
                    </option>
                  ))}
                </select>
                {errors.nearest_medical_center && (
                  <div className="text-danger">
                    {errors.nearest_medical_center}
                  </div>
                )}
              </div>
            </div>

            {/* ── PATIENTS SECTION ── */}
            <div className="mt-4">
              <h5>Patients</h5>

              {/* ── SHARED PATIENT LOCATION / AREA ── */}
              <div className="border rounded p-3 mb-3 bg-light">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">
                      Patient Location<span className="text-danger">*</span>
                    </label>
                    <LocationDropdown
                      value={patientLocation}
                      onChange={handlePatientLocationChange}
                      name="patient_location"
                    />
                    {errors.patient_location_0 && (
                      <small className="text-danger">
                        {errors.patient_location_0}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Patient Area<span className="text-danger">*</span>
                    </label>
                    <AreaDropdown
                      locationId={patientLocation}
                      value={patientArea}
                      onChange={handlePatientAreaChange}
                      name="patient_area"
                    />
                    {errors.patient_area_0 && (
                      <small className="text-danger">
                        {errors.patient_area_0}
                      </small>
                    )}
                  </div>
                  <div className="col-md-12">
                  <label className="form-label">
                    Accident Place <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={accidentPlace}
                    onChange={(e) => {
                      setAccidentPlace(e.target.value);
                      setErrors((prev) => ({ ...prev, accident_place: '' }));
                    }}
                    placeholder="Enter accident place"
                  />
                  {errors.accident_place && (
                    <small className="text-danger">{errors.accident_place}</small>
                  )}
                </div>

                </div>
              </div>

              {/* ── INDIVIDUAL PATIENT DETAILS ── */}
              {patients.map((p, i) => (
                <div
                  key={i}
                  className="border p-3 mt-3 rounded bg-light"
                >
                  <h6 className="fw-bold">Patient {i + 1}</h6>

                  <div className="row g-3">
                    {/* Case Name */}
                    <div className="col-md-6">
                      <label className="form-label">
                        Case Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="case_name"
                        value={p.case_name}
                        onChange={(e) => handlePatientChange(e, i)}
                        className="form-control"
                      />
                      {errors[`case_name_${i}`] && (
                        <small className="text-danger">
                          {errors[`case_name_${i}`]}
                        </small>
                      )}
                    </div>

                    {/* Injury Type */}
                    <div className="col-md-6">
                      <label className="form-label">
                        Injury Type <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="injury_type"
                        value={p.injury_type}
                        onChange={(e) => handlePatientChange(e, i)}
                        list={`injuryTypes_${i}`}
                        className="form-control"
                      />
                      <datalist id={`injuryTypes_${i}`}>
                        {injuryTypes.map((t) => (
                          <option key={t} value={t} />
                        ))}
                      </datalist>
                      {errors[`injury_type_${i}`] && (
                        <small className="text-danger">
                          {errors[`injury_type_${i}`]}
                        </small>
                      )}
                    </div>

                    {/* Triage Card */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Triage Card <span className="text-danger">*</span>
                        </label>
                        <select
                          name="triage_card"
                          value={p.triage_card}
                          onChange={(e) => handlePatientChange(e, i)}
                          className={`form-control form-select ${errors[`triage_card_${i}`] ? 'is-invalid' : ''}`}
                        >
                          <option value="">Select Triage Card</option>
                          <option value="Red">Red</option>
                          <option value="Yellow">Yellow</option>
                          <option value="Green">Green</option>
                          <option value="Black">Black</option>
                        </select>
                        {errors[`triage_card_${i}`] && (
                          <small className="text-danger">{errors[`triage_card_${i}`]}</small>
                        )}
                      </div>


                    {/* Age */}
                    <div className="col-md-6">
                      <label className="form-label">Age</label>
                      <input
                        type="number"
                        name="age"
                        value={p.age}
                        onChange={(e) => handlePatientChange(e, i)}
                        className="form-control"
                      />
                    </div>

                    {/* Gender */}
                    <div className="col-md-6">
                      <label className="form-label">Gender</label>
                      <select
                        name="gender"
                        value={p.gender}
                        onChange={(e) => handlePatientChange(e, i)}
                        className="form-select"
                      >
                        <option value="">Select</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>

                    {/* Contact */}
                    <div className="col-md-6">
                      <label className="form-label">Contact</label>
                      <input
                        type="tel"
                        name="contact_number"
                        value={p.contact_number}
                        onChange={(e) => handlePatientChange(e, i)}
                        className="form-control"
                      />
                      {errors[`contact_number_${i}`] && (
                        <small className="text-danger">
                          {errors[`contact_number_${i}`]}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Remove button */}
                  {patients.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger mt-2"
                      onClick={() => removePatient(i)}
                    >
                      Remove Patient
                    </button>
                  )}
                </div>
              ))}

              {/* Add more patient */}
              <button
                type="button"
                className="btn btn-outline-primary mt-3"
                onClick={addPatient}
              >
                + Add More Patient
              </button>
            </div>
          </div>

          {/* ── FOOTER ── */}
          <div className="modal-footer">
            <button
              className="btn btn-outline-danger"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCaseModal;